import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { checkActiveSubscription, getActiveSubscription, normalizeEmail } from '../utils/subscriptions';

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

const BOOTSTRAP_ADMIN_EMAILS = new Set([
  'successbridgeacademy123@gmail.com',
  'azemalikz27@gmail.com',
  'fasihmalikz123@gmail.com',
  'azemalikz@gmail.com',
]);

function isBootstrapAdminEmail(emailLower) {
  return Boolean(emailLower) && BOOTSTRAP_ADMIN_EMAILS.has(emailLower);
}

async function ensureUserProfile(user) {
  const userRef = doc(db, 'users', user.uid);
  const userSnap = await getDoc(userRef);
  const emailLower = normalizeEmail(user.email);
  const shouldBeAdmin = isBootstrapAdminEmail(emailLower);

  if (userSnap.exists()) {
    const data = userSnap.data();
    const updates = {};

    if (!data.emailLower && emailLower) {
      updates.emailLower = emailLower;
    }
    if (shouldBeAdmin && data.role !== 'admin') {
      updates.role = 'admin';
    }

    if (Object.keys(updates).length > 0) {
      await updateDoc(userRef, updates);
    }

    return shouldBeAdmin || data.role === 'admin';
  }

  await setDoc(userRef, {
    email: user.email || '',
    emailLower,
    displayName: user.displayName || '',
    role: shouldBeAdmin ? 'admin' : 'student',
    createdAt: serverTimestamp(),
  });
  return shouldBeAdmin;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshSubscription = useCallback(async (user, admin) => {
    if (!user) {
      setHasSubscription(false);
      setSubscriptionInfo(null);
      return;
    }

    if (admin) {
      setHasSubscription(true);
      setSubscriptionInfo({
        isAdmin: true,
        daysLeft: 30,
        totalDays: 30,
        planLabel: 'Admin access',
        planId: 'admin',
      });
      return;
    }

    let active = false;
    let info = null;

    try {
      active = await checkActiveSubscription(user, false);
    } catch (err) {
      console.error('Subscription check failed:', err);
    }

    try {
      info = await getActiveSubscription(user, false);
    } catch (err) {
      console.error('Subscription info failed:', err);
    }

    setHasSubscription(active);
    setSubscriptionInfo(info);
  }, []);

  function signUp(email, password, displayName) {
    const normalizedEmail = normalizeEmail(email);
    return createUserWithEmailAndPassword(auth, normalizedEmail, password).then(async (cred) => {
      if (displayName) {
        await updateProfile(cred.user, { displayName });
      }
      return cred;
    });
  }

  async function logIn(email, password, rememberMe = true) {
    await setPersistence(
      auth,
      rememberMe ? browserLocalPersistence : browserSessionPersistence
    );
    return signInWithEmailAndPassword(auth, normalizeEmail(email), password);
  }

  function logOut() {
    return signOut(auth);
  }

  function resetPassword(email) {
    return sendPasswordResetEmail(auth, normalizeEmail(email));
  }

  async function changePassword(currentPassword, newPassword) {
    const user = auth.currentUser;
    if (!user?.email) throw new Error('You must be signed in to change your password.');
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
  }

  useEffect(() => {
    let active = true;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!active) return;
      setCurrentUser(user);
      let admin = false;

      if (user) {
        try {
          admin = await ensureUserProfile(user);
          setIsAdmin(admin);
        } catch (err) {
          console.error('User profile sync failed:', err);
          setIsAdmin(false);
        }

        await refreshSubscription(user, admin);
      } else {
        setIsAdmin(false);
        setHasSubscription(false);
        setSubscriptionInfo(null);
      }

      setLoading(false);
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [refreshSubscription]);

  const value = {
    currentUser,
    isAdmin,
    hasSubscription,
    subscriptionInfo,
    refreshSubscription,
    signUp,
    logIn,
    logOut,
    resetPassword,
    changePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
